import { fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { registryApiPathPrefix, registryServiceURL, routerServiceURL, mainServiceAppURL } from './defines';
import { getCurrentProjectId } from '../../common/context/project-storage';
import { getRouterApiKey } from '../../common/context/router-api-key-storage';

const generateCorrelationId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const baseQueryRegistry = fetchBaseQuery({
  baseUrl: `${registryServiceURL}${registryApiPathPrefix}`,
  credentials: 'include',
  prepareHeaders: (headers) => {
    headers.set('Content-Type', 'application/json');
    headers.set('x-correlation-id', generateCorrelationId());
    const projectId = getCurrentProjectId();
    if (projectId) {
      headers.set('x-project-id', projectId);
      const routerApiKey = getRouterApiKey(projectId);
      if (routerApiKey) {
        headers.set('x-api-key', routerApiKey);
      }
    }
    return headers;
  },
});

export const baseQueryRouter = fetchBaseQuery({
  baseUrl: routerServiceURL,
  credentials: 'include',
  prepareHeaders: (headers) => {
    headers.set('Content-Type', 'application/json');
    headers.set('x-correlation-id', generateCorrelationId());
    const projectId = getCurrentProjectId();
    if (projectId) {
      headers.set('x-project-id', projectId);
    }
    return headers;
  },
});

// Shared refresh state — prevents concurrent refresh attempts from multiple failing queries
let refreshPromise: Promise<boolean> | null = null;

export const attemptTokenRefresh = (): Promise<boolean> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${mainServiceAppURL}/login/native/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) return true;
    } catch {}

    try {
      const res = await fetch(`${mainServiceAppURL}/login/cognito/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) return true;
    } catch {}

    return false;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const wrapWithReauth = (
  baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  return async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error && (result.error as FetchBaseQueryError).status === 401) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        result = await baseQuery(args, api, extraOptions);
      }
    }

    return result;
  };
};

export const baseQueryRegistryWithReauth = wrapWithReauth(baseQueryRegistry);
export const baseQueryRouterWithReauth = wrapWithReauth(baseQueryRouter);
